import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store/index";

/**
 * Local canned response type for client-side state.
 * Different from the backend CannedResponse model in types/models.ts.
 */
export interface LocalCannedResponse {
  id: string;
  title: string;
  content: string;
  userName?: string;
}

interface CannedResponsesState {
  responses: LocalCannedResponse[];
}

const initialState: CannedResponsesState = {
  responses: [],
};

export const cannedResponsesSlice = createSlice({
  name: "cannedResponses",
  initialState,
  reducers: {
    addResponse: (
      state,
      action: PayloadAction<{
        title: string;
        content: string;
        userName?: string;
      }>
    ) => {
      const { title, content, userName } = action.payload;
      state.responses.push({
        id: `canned-${Date.now()}`,
        title,
        content,
        userName: userName || "User",
      });
    },
    editResponse: (
      state,
      action: PayloadAction<{
        id: string;
        title?: string;
        content?: string;
        userName?: string;
      }>
    ) => {
      const { id, title, content, userName } = action.payload;
      const response = state.responses.find((r) => r.id === id);
      if (response) {
        if (title !== undefined) response.title = title;
        if (content !== undefined) response.content = content;
        if (userName !== undefined) response.userName = userName;
      }
    },
    deleteResponse: (state, action: PayloadAction<{ id: string }>) => {
      const index = state.responses.findIndex(
        (r) => r.id === action.payload.id
      );
      if (index !== -1) {
        state.responses.splice(index, 1);
      }
    },
    resetResponses: (state) => {
      state.responses = initialState.responses;
    },
  },
});

export const { addResponse, editResponse, deleteResponse, resetResponses } =
  cannedResponsesSlice.actions;

// Selector to get username from auth state
export const selectUsername = (state: RootState) =>
  (state.auth.userInfo?.username || state.auth.userInfo?.full_name) ?? "User";

export default cannedResponsesSlice.reducer;
